import {
  type BadgeTone,
  badgeToneClass,
  badgeToneDotBg,
} from "components/ui/StatusBadge/StatusBadge";
import type { PipelineEmailStatus } from "hooks/use-pipeline-email-api";
import { cn } from "lib/utils";
import type { FC } from "react";

export type EmailStatusDensity = "minimal" | "normal" | "maximal";

type StatusMeta = {
  label: string;
  tone: BadgeTone;
};

/**
 * ステータス毎のラベルとトーンを返す
 * @param {PipelineEmailStatus} status ステータス
 * @return {StatusMeta} 表示メタ情報
 */
function resolveStatusMeta(status: PipelineEmailStatus): StatusMeta {
  switch (status) {
    case "not_sent":
      return { label: "未送信", tone: "soft-neutral" };
    case "waiting_reply":
      return { label: "返信待ち", tone: "soft-warning" };
    case "has_reply":
      return { label: "返信あり", tone: "soft-success" };
    case "stale":
      return { label: "要フォロー", tone: "soft-error" };
  }
}

export type EmailStatusBadgeProps = {
  status: PipelineEmailStatus;
  /** 経過時間（時間単位）。null なら経過時間は非表示 */
  elapsedHours: number | null;
  /** 直近メールの件名（maximal 密度のときのみ表示） */
  lastSubject: string | null;
  /** 未対応返信数。1 以上のとき視覚的に強調 */
  unrespondedReplies: number;
  /** 表示密度 */
  density?: EmailStatusDensity;
};

/**
 * 経過時間を短い日本語表記で表現する
 * @param {number|null} hours 時間単位の経過時間
 * @return {string} 例: "3d", "5h", "-"
 */
function formatElapsed(hours: number | null): string {
  if (hours == null) return "";
  if (hours < 1) return "<1h";
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

/**
 * Sales Board カード/行に出すメール状況バッジ。密度で表示量を切り替える。
 * @param {EmailStatusBadgeProps} props props
 * @return {JSX.Element} バッジ
 */
export const EmailStatusBadge: FC<EmailStatusBadgeProps> = ({
  status,
  elapsedHours,
  lastSubject,
  unrespondedReplies,
  density = "normal",
}) => {
  const meta = resolveStatusMeta(status);
  const elapsed = formatElapsed(elapsedHours);

  return (
    <div className="inline-flex items-center gap-1.5 text-xs">
      <span
        className={cn(badgeToneClass[meta.tone], "gap-1.5")}
        title={lastSubject || undefined}
      >
        <span
          className={cn(
            "inline-block w-2 h-2 rounded-full",
            badgeToneDotBg[meta.tone],
          )}
        />
        {meta.label}
        {density !== "minimal" && elapsed && (
          <span className="text-[10px] opacity-70">{elapsed}</span>
        )}
      </span>
      {unrespondedReplies > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-rose-500 text-white">
          {unrespondedReplies}
        </span>
      )}
      {density === "maximal" && lastSubject && (
        <span className="text-[10px] text-slate-500 truncate max-w-[16ch]">
          {lastSubject}
        </span>
      )}
    </div>
  );
};
